import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import { DateSelectorPopover } from '@/components/ui/date-selector/date-selector-popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Trade } from '@/models/registry';
import { tradeStore } from '@/models/stores/trades';
import { useMutation } from '@tanstack/react-query';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';

export function TradePayoffDialog({
  trade,
  children,
  open,
  setOpen,
}: {
  trade: Trade;
  children?: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const [payoffAmount, setPayoffAmount] = useState(
    trade.payoff_amount ? (trade.payoff_amount / 100).toFixed(2) : '',
  );
  const [payoffExpiration, setPayoffExpiration] = useState<Date | undefined>(
    trade.payoff_expiration ? new Date(trade.payoff_expiration) : undefined,
  );

  const updatePayoffMutation = useMutation({
    mutationFn: async ({
      tradeId,
      data,
    }: {
      tradeId: string;
      data: { payoff_amount: number; payoff_expiration: string | null };
    }) => {
      const response = await pdkAxios.post(`/trades/${tradeId}/payoff`, data);
      return response.data;
    },
    onMutate: async ({ data }) => {
      // Optimistically update the store
      tradeStore.update({
        id: trade.id,
        trade: {
          payoff_amount: data.payoff_amount,
          payoff_expiration: data.payoff_expiration,
        },
      });
    },
    onSuccess: () => {
      toast({
        title: 'Payoff updated',
        description: 'The payoff information has been updated successfully',
      });
      setOpen(false);
    },
    onError: (error: any) => {
      console.error('Error updating payoff:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          'Failed to update payoff information',
        variant: 'destructive',
      });

      // Revert the optimistic update
      tradeStore.update({
        id: trade.id,
        trade: {
          payoff_amount: trade.payoff_amount,
          payoff_expiration: trade.payoff_expiration,
        },
      });
    },
  });

  const handleSave = () => {
    const amount = parseFloat(payoffAmount) * 100; // Convert to cents

    if (isNaN(amount) || amount < 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid payoff amount',
        variant: 'destructive',
      });
      return;
    }

    updatePayoffMutation.mutate({
      tradeId: trade.id,
      data: {
        payoff_amount: amount,
        payoff_expiration: payoffExpiration
          ? payoffExpiration.toISOString()
          : null,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-[425px]"
        onClickClose={() => setOpen(false)}
      >
        <DialogHeader>
          <DialogTitle>Update Payoff</DialogTitle>
          <DialogDescription>
            Update the payoff amount and expiration date for this trade.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label className="text-sm">Payoff Amount</label>
            <CurrencyInput
              value={payoffAmount}
              onChange={(e) => setPayoffAmount(e.target.value)}
              placeholder="0.00"
              wrapperClassName="w-full"
              className="w-full"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm">Payoff Expiration</label>
            <DateSelectorPopover
              date={payoffExpiration}
              onSelectDate={setPayoffExpiration}
              placeholder="Select expiration date"
              icon={<CalendarIcon className="h-3.5 w-3.5" />}
              withInput
              className="w-full"
              side="bottom"
              align="start"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={updatePayoffMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSave}
            disabled={updatePayoffMutation.isPending}
          >
            {updatePayoffMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
